// All copy for /founder lives here — section components map over these arrays/objects, so
// adding a press item or journey entry is one object added here, never a JSX change.
// Sourced from the "Founder v4.dc.html" design handoff (client-provided), not invented.
// TODO(content): anywhere marked below still needs a real asset or a fact sign-off.

export const HERO_EYEBROW = 'Founder & CEO, Vilva Networks';
export const HERO_SUBTITLE =
  'Started in 2012 with a simple belief — Tamil is identity, culture, and love. From Tamil on T-shirts to GrowthOS powering 100+ businesses.';

export const HERO_BADGES = ['Best Consultant Award 2026'];

export interface OutletLogo {
  name: string;
  logo: string;
}

export const RECOGNISED_IN: OutletLogo[] = [
  { name: 'The Hindu', logo: '/images/founder/press-logos/the-hindu.png' },
  { name: 'Nanayam Vikatan', logo: '/images/founder/press-logos/nanayam-vikatan.png' },
  { name: 'YourStory Tamil', logo: '/images/founder/press-logos/tamil-yourstory.png' },
  { name: 'WhatsApp Business Blog', logo: '/images/founder/press-logos/whatsapp-business.png' },
  { name: 'Puthiya Thalaimurai', logo: '/images/founder/press-logos/puthiya-thalaimurai.png' },
  { name: 'Valar Thozhil', logo: '/images/founder/press-logos/valar-thozhil.webp' },
  { name: 'The Indian Express', logo: '/images/founder/press-logos/indian-express.png' },
];

export interface StatItem {
  label: string;
  value: string;
  icon: 'years' | 'media' | 'students' | 'speaking';
}

export const IMPACT_STATS: StatItem[] = [
  { icon: 'years', value: '14+', label: 'Years of Experience' },
  { icon: 'media', value: '7', label: 'Media Features' },
  { icon: 'students', value: '1800+', label: 'Students Addressed' },
  { icon: 'speaking', value: '7+', label: 'Speaking Engagements' },
];

export const STATS_INTRO = "Numbers behind the founder's decade-plus of building Global businesses.";
export const STATS_FOOTNOTE = 'Helping businesses, teams and individuals achieve sustainable growth.';


export interface JourneyEntry {
  period: string;
  title: string;
  description: string;
  image: string;
  imageAlt: string;
}

export const JOURNEY: JourneyEntry[] = [
  {
    period: '2012',
    title: 'A simple beginning',
    description:
      'Started with a simple belief — Tamil is more than a language; it is identity, culture, and love. Launched a Tamil T-shirt label with our own designs.',
    image: '/images/founder/press/rajendra-chola-quote.jpg',
    imageAlt: 'Gopinath Ravirajan, Co-Founder, Vilva Clothing, quoted on the 1000th year Rajendra Chola celebrations T-shirts',
  },
  {
    period: '2013 - 2014',
    title: 'Finding our audience',
    description:
      'Our T-shirts connected with students, professionals and Tamil enthusiasts. Word of mouth helped us grow beyond our expectations.',
    image: '/images/founder/press/valar-thozhil.jpg',
    imageAlt: 'Valar Thozhil magazine spread showing the Tamil typography T-shirt range',
  },
  {
    period: '2015 - 2017',
    title: 'Expanding possibilities',
    description:
      'Moved from just T-shirts to a wider range of Tamil merchandise. Strengthened our community and built a loyal customer base.',
    image: '/images/founder/press/the-hindu-metroplus.jpg',
    imageAlt: 'The Hindu MetroPlus front page, Tamil to a Tee, February 2014',
  },
  {
    period: '2026',
    title: 'Best Consultant Award',
    description: 'Celebrating the Best Consultant Award 2026 with the Vilva team.',
    image: '/images/founder/team-photo.png',
    imageAlt: 'The Vilva team celebrating with the Best Consultant Award 2026',
  },
];

export const BELIEF_HEADING_LINE1 = 'Rooted in Tamil Nadu.';
export const BELIEF_HEADING_LINE2 = 'Building for a bigger tomorrow.';
export const BELIEF_BODY = 'A small beginning, a deep-rooted belief, and the courage to build something bigger.';

export const TEAM_CAPTION =
  'Recognized & Trusted Since 2015 — the Vilva team celebrating a decade of building Tamil Nadu businesses.';
export const TEAM_INTRO =
  "The people behind Vilva's work — recognized for 14 years of building Tamil Nadu businesses.";
export const GROWTHOS_HEADING = 'Built Around GrowthOS';
export const GROWTHOS_BODY =
  "We don't hand off pieces of your growth to five different vendors. GrowthOS is Vilva's own operating model — one team that owns brand, content, performance ads, WhatsApp commerce, and the portals or apps behind them. Whatever a business needs next to grow, we build or run it in-house, so nothing falls through the cracks between agencies.";

export interface PressCard {
  outlet: string;
  meta: string;
  headline: string;
  summary: string;
  /** TODO(content): omit until a real clipping/photo is sourced — renders an empty slot instead of a broken image. */
  image?: string;
  imageAlt?: string;
  /** Outlet's own logo (colour), shown as a small badge on the card. Omit if none sourced. */
  logo?: string;
  /** Only the Hindu MetroPlus item has an embedded pull-quote. */
  quote?: string;
  quoteAttribution?: string;
}

// One unified set — the "featured" card at the top of the Press section is just PRESS_ITEMS[0]
// by default; PressMedia.tsx swaps the featured content to whichever index is active as the
// user navigates the carousel, rather than pinning it permanently to this first item.
export const PRESS_ITEMS: PressCard[] = [
  {
    outlet: 'The Hindu MetroPlus',
    meta: 'Feb 2014',
    headline: 'The Hindu MetroPlus Feature Profile',
    summary:
      'Featured nationally under "Tamil to a Tee", documenting the cultural resurgence sparked by indigenous typography on modern casual apparel.',
    image: '/images/founder/press/the-hindu-metroplus.jpg',
    imageAlt: 'The Hindu MetroPlus front page, Tamil to a Tee, February 2014',
    logo: '/images/founder/press-logos/the-hindu.png',
    quote: 'Our T-shirts for the 1000th year celebrations of Rajendra Chola became popular in Singapore as much as Tamil Nadu.',
    quoteAttribution: 'Gopinath Ravirajan, Co-Founder, Vilva Clothing',
  },
  {
    outlet: 'Neeya Naana',
    meta: 'Vijay TV',
    headline: 'Speaking on Neeya Naana',
    summary: 'Attended and spoke during the Neeya Naana program on Vijay TV, sharing his views as a participant.',
    image: '/images/founder/press/neeya-naana.jpg',
    imageAlt: 'Gopinath Ravirajan speaking as a participant on Neeya Naana on Vijay TV',
  },
  {
    outlet: 'Valar Thozhil',
    meta: 'Dec 2015',
    headline: 'Entrepreneur profile',
    summary: 'A two-page entrepreneur profile in Valar Thozhil magazine.',
    image: '/images/founder/press/valar-thozhil.jpg',
    imageAlt: 'Valar Thozhil magazine entrepreneur profile spread, December 2015',
    logo: '/images/founder/press-logos/valar-thozhil.webp',
  },
  {
    outlet: 'YourStory',
    meta: 'Tamil Feature',
    headline: 'Tamil to the world, and the world to Tamil',
    summary: 'Fans of the Tamil-themed T-shirt label as far as Malaysia, Australia, and Singapore.',
    image: '/images/founder/press/yourstory-tamil.jpg',
    imageAlt: 'YourStory Tamil feature page on Gopinath Ravirajan',
    logo: '/images/founder/press-logos/tamil-yourstory.png',
  },
  {
    outlet: 'Vikatan',
    meta: 'Interview',
    headline: 'Scaling Vilva Clothing',
    summary: 'Interview with co-founders Arun and Gopinath Ravirajan on scaling Vilva Clothing.',
    image: '/images/founder/press/vikatan.jpg',
    imageAlt: 'Vikatan interview page with co-founders Arun and Gopinath Ravirajan',
    logo: '/images/founder/press-logos/nanayam-vikatan.png',
  },
  {
    // TODO(content): headline, summary and clipping/photo still needed — user said more details incoming.
    outlet: 'The Indian Express',
    meta: 'Feature',
    headline: 'The Indian Express feature',
    summary: 'Vilva Clothing featured in The Indian Express.',
    image: '/images/founder/press/indian-express.jpg',
    imageAlt: 'The Indian Express clipping featuring Vilva Clothing',
    logo: '/images/founder/press-logos/indian-express-white.png',
  },
  {
    outlet: 'Puthiya Thalaimurai',
    meta: 'Print Feature',
    headline: 'Reaching customers through digital marketing',
    summary: 'Arun and Gopinath Ravirajan share insights on using social media and digital marketing to connect businesses with their customers.',
    image: '/images/founder/press/puthiya-thalaimurai.jpg',
    imageAlt: 'Puthiya Thalaimurai Tamil print feature on digital marketing with Arun and Gopinath Ravirajan',
    logo: '/images/founder/press-logos/puthiya-thalaimurai.png',
  },
  {
    outlet: 'WhatsApp Business',
    meta: 'Business Blog',
    headline: 'Featured on the WhatsApp Business Blog',
    summary: 'The Vilva business journey featured on the WhatsApp Business Blog.',
    logo: '/images/founder/press-logos/whatsapp-business.png',
  },
];

export interface Encounter {
  name: string;
  role: string;
  quote: string;
  /** TODO(content): photo not sourced for this encounter yet. */
  image?: string;
  imageAlt?: string;
}

// Real photos, but not every name/role/quote is confirmed yet — entries marked
// TODO(content) below use the literal placeholder text "Name" / "Role" until the
// real details are supplied, per the client's own instruction to ship the photo now
// and fill the name in later. Never invent a specific name for these.
// Entries with a real photo lead the carousel — the first entry is what renders by
// default in the featured card, so a photo-less placeholder shouldn't sit there.
export const ENCOUNTERS: Encounter[] = [
  {
    name: 'Mr. Shiva Ayyadurai',
    role: 'Inventor of Email',
    quote: 'A memorable meeting with Mr. Shiva Ayyadurai, connecting the Vilva journey with the world of technology and innovation.',
    image: '/images/founder/encounters/e2-shiva-ayyadurai.jpg',
    imageAlt: 'Gopinath Ravirajan at the Sathyabama University Power Lecture Series with Dr. V.A. Shiva Ayyadurai, inventor of email',
  },
    {
    name: 'Mr. Arunachalam Muruganantham',
    role: "Padma Shri 2016 · The real 'Pad Man'",
    image: '/images/founder/encounters/e1-unnamed-meeting.jpg',
    quote: 'A memorable encounter with Arunachalam Muruganantham, whose work is a reminder of how practical ideas can address everyday challenges.', 
    imageAlt: 'Gopinath Ravirajan at the Arunachalam Muruganantham',
  },
  {
    name: 'Actor Mr. Vijay', // TODO(content): real name needed
    role: 'World Environment Day', // TODO(content): real role needed
    quote: 'Marking World Environment Day alongside actor Vijay, with a shared focus on caring for our environment.',
    image: '/images/founder/encounters/e3-group-event.jpg',
    imageAlt: 'Gopinath Ravirajan at an event on stage with a group',
  },


];

export const COLLEGES_EYEBROW = 'College Engagements';
export const COLLEGES_HEADING_PLAIN = 'Inspiring the ';
export const COLLEGES_HEADING_ACCENT = 'Next Generation';
export const COLLEGES_INTRO = 'Sessions with students at colleges and schools across Tamil Nadu.';

export const COLLEGE_FEATURED = {
  image: '/images/founder/education/ts-srinivasan-centre.jpg',
  imageAlt: 'Students attending a session in a lecture hall at T.S. Srinivasan Polytechnic College',
  caption: 'T.S. Srinivasan Polytechnic College',
};

export const COLLEGE_INSTITUTIONS = [
  'Sree Sankara Arts and Science',
  'Sathyabama Institute of Science and Technology',
  'T.S. Srinivasan Polytechnic College',
];

export const SCHOOL_INSTITUTIONS = [
  'GT Aloha Vidya Mandir, ECR — Guest of Honour, 2016',
  'Smt. NDJA Vivekananda Vidyalaya Jr. College, M.K.B Nagar — Guest of Honour, 26 Jan 2016',
];

export const CLOSING_EYEBROW = 'Proudly Made in Tamil Nadu';
export const CLOSING_HEADING_PLAIN = 'From Tamil Nadu.';
export const CLOSING_HEADING_ACCENT = 'For the world.';

export interface BeliefPillar {
  icon: 'home' | 'globe' | 'handshake' | 'world';
  title: string;
  bodyLine1: string;
  bodyLine2: string;
}

export const BELIEF_PILLARS: BeliefPillar[] = [
  { icon: 'home', title: 'Made in Tamil Nadu', bodyLine1: 'Rooted in our land,', bodyLine2: 'built for tomorrow.' },
  { icon: 'globe', title: 'Heartbeat of Global Tech', bodyLine1: 'Modern solutions for', bodyLine2: 'a connected world.' },
  { icon: 'handshake', title: 'Build Together', bodyLine1: 'Stronger teams.', bodyLine2: 'Greater possibilities.' },
  { icon: 'world', title: 'For the World', bodyLine1: 'Local values.', bodyLine2: 'Global impact.' },
];

export const CLOSING_ATTRIBUTION = 'Gopinath Ravirajan • CEO';

export interface SocialLink {
  platform: 'phone' | 'email' | 'whatsapp' | 'linkedin' | 'website';
  href: string;
  label: string;
}

export const CLOSING_SOCIAL: SocialLink[] = [
  { platform: 'phone', href: 'tel:+919176569459', label: 'Call +91 91765 69459' },
  { platform: 'email', href: 'mailto:info@vilvabusiness.com', label: 'Email info@vilvabusiness.com' },
  { platform: 'whatsapp', href: 'https://wa.me/919176569459', label: 'WhatsApp' },
  { platform: 'linkedin', href: 'https://www.linkedin.com/in/gopinathravirajan', label: 'LinkedIn' },
  { platform: 'website', href: 'https://vilvabusiness.com', label: 'vilvabusiness.com' },
];

export const CTAS = {
  inviteToSpeak: { label: 'Invite to Speak', href: '/contact-us' },
};

export const TAMIL_BRAND_STORY = {
  eyebrow: 'A Tamil identity evangelist',
  heading: 'Tamil pride,',
  accent: 'woven into a brand.',
  introduction: 'As a Tamil identity evangelist, Gopinath brings his pride in the language and culture into everyday life - through the clothes people wear and the labels they carry.',
  body: 'Through Vilva Clothing, he brought that belief into the brand itself. From Tamil-inspired designs to a label that reads Made in Tamil Nadu, every detail became a way to express where the brand comes from and what it stands for.',
  closing: 'Made in Tamil Nadu. Worn with pride.',
  image: '/images/founder/made-in-tamil-nadu-label.jpg',
  imageAlt: 'Vilva Clothing shirt label with Made in Tamil Nadu highlighted',
};
